import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { performanceAPI, employeeAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Target, Plus, CheckCircle2, Clock, AlertCircle, Star, 
  TrendingUp, Calendar, ChevronRight, Loader2, Send, Eye, Edit2, Trash2, Users 
} from 'lucide-react';

const RATING_LABELS = {
  1: 'Poor',
  2: 'Below Expectations',
  3: 'Meets Expectations',
  4: 'Exceeds Expectations',
  5: 'Exceptional'
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800'
};

const REVIEW_STATUS_COLORS = {
  not_started: 'bg-gray-100 text-gray-800',
  self_review_pending: 'bg-blue-100 text-blue-800',
  manager_review_pending: 'bg-yellow-100 text-yellow-800',
  hr_review_pending: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800'
};

export default function PerformancePage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('my-goals');
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState([]);
  const [myReview, setMyReview] = useState(null);
  const [currentCycle, setCurrentCycle] = useState(null);
  const [teamReviews, setTeamReviews] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showCycleDialog, setShowCycleDialog] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewType, setReviewType] = useState(null); // 'self', 'manager', 'hr'

  const isHR = ['hr', 'admin', 'super_admin'].includes(user?.role);
  const isManager = user?.role === 'manager' || isHR;

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'my-goals') {
        const [goalsRes, reviewRes] = await Promise.all([
          performanceAPI.getMyGoals(),
          performanceAPI.getMyCurrentReview()
        ]);
        setGoals(goalsRes.data);
        setMyReview(reviewRes.data.review);
        setCurrentCycle(reviewRes.data.cycle);
      } else if (activeTab === 'team-reviews' && isManager) {
        const [teamRes, statsRes] = await Promise.all([
          performanceAPI.getTeamReviews(),
          performanceAPI.getMyTeamStats()
        ]);
        setTeamReviews(teamRes.data);
      } else if (activeTab === 'hr-dashboard' && isHR) {
        const [summaryRes, cyclesRes, pendingRes] = await Promise.all([
          performanceAPI.getSummary(),
          performanceAPI.getCycles(),
          performanceAPI.getReviews({ pending_hr: true })
        ]);
        setSummary(summaryRes.data);
        setCycles(cyclesRes.data);
        setTeamReviews(pendingRes.data);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  // Goal Dialog Component
  const GoalDialog = () => {
    const [form, setForm] = useState({
      title: '',
      description: '',
      category: 'Individual',
      year: new Date().getFullYear(),
      quarter: Math.ceil((new Date().getMonth() + 1) / 3),
      key_results: [{ title: '', target_value: 100, unit: '%', weight: 1 }]
    });
    const [saving, setSaving] = useState(false);

    const addKeyResult = () => {
      setForm(prev => ({
        ...prev,
        key_results: [...prev.key_results, { title: '', target_value: 100, unit: '%', weight: 1 }]
      }));
    };

    const updateKeyResult = (index, field, value) => {
      setForm(prev => ({
        ...prev,
        key_results: prev.key_results.map((kr, i) => 
          i === index ? { ...kr, [field]: value } : kr
        )
      }));
    };

    const removeKeyResult = (index) => {
      if (form.key_results.length > 1) {
        setForm(prev => ({
          ...prev,
          key_results: prev.key_results.filter((_, i) => i !== index)
        }));
      }
    };

    const handleSubmit = async () => {
      if (!form.title.trim()) {
        toast.error('Please enter a goal title');
        return;
      }
      if (form.key_results.some(kr => !kr.title.trim())) {
        toast.error('Please fill in all key results');
        return;
      }

      setSaving(true);
      try {
        await performanceAPI.createGoal(form);
        toast.success('Goal created successfully');
        setShowGoalDialog(false);
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create goal');
      } finally {
        setSaving(false);
      }
    };

    return (
      <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Goal (OKR)</DialogTitle>
            <DialogDescription>Define your objective and measurable key results</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Objective Title *</Label>
              <Input
                placeholder="e.g., Improve customer satisfaction"
                value={form.title}
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                data-testid="goal-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe what you want to achieve..."
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Individual">Individual</SelectItem>
                    <SelectItem value="Team">Team</SelectItem>
                    <SelectItem value="Company">Company</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Year</Label>
                <Select value={String(form.year)} onValueChange={(v) => setForm(prev => ({ ...prev, year: parseInt(v) }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(new Date().getFullYear() - 1)}>{new Date().getFullYear() - 1}</SelectItem>
                    <SelectItem value={String(new Date().getFullYear())}>{new Date().getFullYear()}</SelectItem>
                    <SelectItem value={String(new Date().getFullYear() + 1)}>{new Date().getFullYear() + 1}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quarter</Label>
                <Select value={String(form.quarter)} onValueChange={(v) => setForm(prev => ({ ...prev, quarter: parseInt(v) }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Q1 (Jan-Mar)</SelectItem>
                    <SelectItem value="2">Q2 (Apr-Jun)</SelectItem>
                    <SelectItem value="3">Q3 (Jul-Sep)</SelectItem>
                    <SelectItem value="4">Q4 (Oct-Dec)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Key Results</Label>
                <Button variant="outline" size="sm" onClick={addKeyResult}>
                  <Plus className="w-4 h-4 mr-1" /> Add KR
                </Button>
              </div>
              
              {form.key_results.map((kr, index) => (
                <div key={index} className="p-3 border rounded-lg space-y-3 bg-muted/30">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">KR {index + 1}</span>
                    {form.key_results.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeKeyResult(index)} className="ml-auto h-6 w-6 p-0">
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    )}
                  </div>
                  <Input
                    placeholder="Key result title"
                    value={kr.title}
                    onChange={(e) => updateKeyResult(index, 'title', e.target.value)}
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Target</Label>
                      <Input
                        type="number"
                        value={kr.target_value}
                        onChange={(e) => updateKeyResult(index, 'target_value', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Unit</Label>
                      <Select value={kr.unit} onValueChange={(v) => updateKeyResult(index, 'unit', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="%">%</SelectItem>
                          <SelectItem value="count">Count</SelectItem>
                          <SelectItem value="$">$</SelectItem>
                          <SelectItem value="₹">₹</SelectItem>
                          <SelectItem value="hours">Hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Weight</Label>
                      <Input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={kr.weight}
                        onChange={(e) => updateKeyResult(index, 'weight', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGoalDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="save-goal-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
              Create Goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // Self Review Dialog
  const SelfReviewDialog = () => {
    const [form, setForm] = useState({
      achievements: '',
      challenges: '',
      development_areas: '',
      goal_ratings: [],
      overall_self_rating: 3
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
      if (selectedReview && myReview?.goals) {
        setForm(prev => ({
          ...prev,
          goal_ratings: myReview.goals.map(g => ({
            item_id: g.id,
            item_title: g.title,
            rating: 3,
            comments: ''
          }))
        }));
      }
    }, [selectedReview]);

    const handleSubmit = async () => {
      if (!form.achievements.trim() || form.achievements.length < 10) {
        toast.error('Please describe your achievements (minimum 10 characters)');
        return;
      }

      setSaving(true);
      try {
        await performanceAPI.submitSelfReview(selectedReview.id, form);
        toast.success('Self review submitted successfully');
        setShowReviewDialog(false);
        setSelectedReview(null);
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to submit review');
      } finally {
        setSaving(false);
      }
    };

    return (
      <Dialog open={showReviewDialog && reviewType === 'self'} onOpenChange={(open) => { if (!open) { setShowReviewDialog(false); setSelectedReview(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Self Review</DialogTitle>
            <DialogDescription>Complete your self-assessment for this review cycle</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Key Achievements *</Label>
              <Textarea
                placeholder="Describe your key achievements during this period..."
                value={form.achievements}
                onChange={(e) => setForm(prev => ({ ...prev, achievements: e.target.value }))}
                rows={4}
                data-testid="achievements-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Challenges Faced</Label>
              <Textarea
                placeholder="What challenges did you face?"
                value={form.challenges}
                onChange={(e) => setForm(prev => ({ ...prev, challenges: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Development Areas</Label>
              <Textarea
                placeholder="Areas where you want to improve..."
                value={form.development_areas}
                onChange={(e) => setForm(prev => ({ ...prev, development_areas: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Overall Self Rating *</Label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <Button
                    key={rating}
                    variant={form.overall_self_rating === rating ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setForm(prev => ({ ...prev, overall_self_rating: rating }))}
                    className="flex-col h-auto py-2"
                  >
                    <Star className={`w-4 h-4 ${form.overall_self_rating >= rating ? 'fill-current' : ''}`} />
                    <span className="text-xs mt-1">{rating}</span>
                  </Button>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">{RATING_LABELS[form.overall_self_rating]}</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReviewDialog(false); setSelectedReview(null); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="submit-self-review-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // Manager Review Dialog
  const ManagerReviewDialog = () => {
    const [form, setForm] = useState({
      goal_ratings: [],
      strengths: '',
      improvement_areas: '',
      overall_rating: 3,
      promotion_recommendation: false,
      comments: ''
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async () => {
      if (!form.strengths.trim() || form.strengths.length < 10) {
        toast.error('Please describe employee strengths (minimum 10 characters)');
        return;
      }
      if (!form.improvement_areas.trim() || form.improvement_areas.length < 10) {
        toast.error('Please describe improvement areas (minimum 10 characters)');
        return;
      }

      setSaving(true);
      try {
        await performanceAPI.submitManagerReview(selectedReview.id, form);
        toast.success('Manager review submitted successfully');
        setShowReviewDialog(false);
        setSelectedReview(null);
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to submit review');
      } finally {
        setSaving(false);
      }
    };

    return (
      <Dialog open={showReviewDialog && reviewType === 'manager'} onOpenChange={(open) => { if (!open) { setShowReviewDialog(false); setSelectedReview(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manager Review - {selectedReview?.employee_name}</DialogTitle>
            <DialogDescription>Complete your assessment for this employee</DialogDescription>
          </DialogHeader>

          {selectedReview?.self_review && (
            <div className="bg-muted/50 p-4 rounded-lg mb-4">
              <h4 className="font-medium mb-2">Employee's Self Review</h4>
              <p className="text-sm text-muted-foreground">{selectedReview.self_review.achievements}</p>
              <p className="text-sm mt-2">Self Rating: <Badge>{selectedReview.self_review.overall_self_rating}/5</Badge></p>
            </div>
          )}

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Strengths *</Label>
              <Textarea
                placeholder="What are this employee's key strengths?"
                value={form.strengths}
                onChange={(e) => setForm(prev => ({ ...prev, strengths: e.target.value }))}
                rows={3}
                data-testid="strengths-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Areas for Improvement *</Label>
              <Textarea
                placeholder="Where can this employee improve?"
                value={form.improvement_areas}
                onChange={(e) => setForm(prev => ({ ...prev, improvement_areas: e.target.value }))}
                rows={3}
                data-testid="improvement-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Overall Rating *</Label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <Button
                    key={rating}
                    variant={form.overall_rating === rating ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setForm(prev => ({ ...prev, overall_rating: rating }))}
                    className="flex-col h-auto py-2"
                  >
                    <Star className={`w-4 h-4 ${form.overall_rating >= rating ? 'fill-current' : ''}`} />
                    <span className="text-xs mt-1">{rating}</span>
                  </Button>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">{RATING_LABELS[form.overall_rating]}</p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="promotion"
                checked={form.promotion_recommendation}
                onChange={(e) => setForm(prev => ({ ...prev, promotion_recommendation: e.target.checked }))}
                className="rounded"
              />
              <Label htmlFor="promotion">Recommend for promotion</Label>
            </div>

            <div className="space-y-2">
              <Label>Additional Comments</Label>
              <Textarea
                placeholder="Any additional feedback..."
                value={form.comments}
                onChange={(e) => setForm(prev => ({ ...prev, comments: e.target.value }))}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReviewDialog(false); setSelectedReview(null); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="submit-manager-review-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // HR Review Dialog  
  const HRReviewDialog = () => {
    const [form, setForm] = useState({
      calibrated_rating: 3,
      calibration_notes: '',
      final_comments: ''
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async () => {
      setSaving(true);
      try {
        await performanceAPI.submitHRReview(selectedReview.id, form);
        toast.success('HR review completed successfully');
        setShowReviewDialog(false);
        setSelectedReview(null);
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to submit review');
      } finally {
        setSaving(false);
      }
    };

    return (
      <Dialog open={showReviewDialog && reviewType === 'hr'} onOpenChange={(open) => { if (!open) { setShowReviewDialog(false); setSelectedReview(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>HR Review & Calibration - {selectedReview?.employee_name}</DialogTitle>
            <DialogDescription>Finalize the performance rating</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedReview?.self_review && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Self Review Summary</h4>
                <p className="text-sm">{selectedReview.self_review.achievements?.substring(0, 200)}...</p>
                <p className="text-sm mt-2">Self Rating: <Badge variant="outline">{selectedReview.self_review.overall_self_rating}/5</Badge></p>
              </div>
            )}

            {selectedReview?.manager_review && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Manager Review Summary</h4>
                <p className="text-sm"><strong>Strengths:</strong> {selectedReview.manager_review.strengths?.substring(0, 150)}...</p>
                <p className="text-sm mt-1"><strong>Improvement:</strong> {selectedReview.manager_review.improvement_areas?.substring(0, 150)}...</p>
                <p className="text-sm mt-2">Manager Rating: <Badge variant="outline">{selectedReview.manager_review.overall_rating}/5</Badge></p>
                {selectedReview.manager_review.promotion_recommendation && (
                  <Badge className="mt-2 bg-green-500">Recommended for Promotion</Badge>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Calibrated Final Rating *</Label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <Button
                    key={rating}
                    variant={form.calibrated_rating === rating ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setForm(prev => ({ ...prev, calibrated_rating: rating }))}
                    className="flex-col h-auto py-2"
                  >
                    <Star className={`w-4 h-4 ${form.calibrated_rating >= rating ? 'fill-current' : ''}`} />
                    <span className="text-xs mt-1">{rating}</span>
                  </Button>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">{RATING_LABELS[form.calibrated_rating]}</p>
            </div>

            <div className="space-y-2">
              <Label>Calibration Notes</Label>
              <Textarea
                placeholder="Notes on rating calibration decisions..."
                value={form.calibration_notes}
                onChange={(e) => setForm(prev => ({ ...prev, calibration_notes: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Final Comments</Label>
              <Textarea
                placeholder="Any final comments for the record..."
                value={form.final_comments}
                onChange={(e) => setForm(prev => ({ ...prev, final_comments: e.target.value }))}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReviewDialog(false); setSelectedReview(null); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="submit-hr-review-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Finalize Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // Review Cycle Dialog (HR Only)
  const CycleDialog = () => {
    const currentYear = new Date().getFullYear();
    const [form, setForm] = useState({
      name: `Q${Math.ceil((new Date().getMonth() + 1) / 3)} ${currentYear} Review`,
      cycle_type: 'quarterly',
      year: currentYear,
      quarter: Math.ceil((new Date().getMonth() + 1) / 3),
      start_date: '',
      end_date: '',
      self_review_deadline: '',
      manager_review_deadline: '',
      is_active: true
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async () => {
      if (!form.start_date || !form.end_date || !form.self_review_deadline || !form.manager_review_deadline) {
        toast.error('Please fill in all dates');
        return;
      }

      setSaving(true);
      try {
        await performanceAPI.createCycle(form);
        toast.success('Review cycle created successfully');
        setShowCycleDialog(false);
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create cycle');
      } finally {
        setSaving(false);
      }
    };

    return (
      <Dialog open={showCycleDialog} onOpenChange={setShowCycleDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Review Cycle</DialogTitle>
            <DialogDescription>Set up a new performance review period</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Cycle Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="cycle-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.cycle_type} onValueChange={(v) => setForm(prev => ({ ...prev, cycle_type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.cycle_type === 'quarterly' && (
                <div className="space-y-2">
                  <Label>Quarter</Label>
                  <Select value={String(form.quarter)} onValueChange={(v) => setForm(prev => ({ ...prev, quarter: parseInt(v) }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Q1</SelectItem>
                      <SelectItem value="2">Q2</SelectItem>
                      <SelectItem value="3">Q3</SelectItem>
                      <SelectItem value="4">Q4</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Self Review Deadline</Label>
                <Input type="date" value={form.self_review_deadline} onChange={(e) => setForm(prev => ({ ...prev, self_review_deadline: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Manager Review Deadline</Label>
                <Input type="date" value={form.manager_review_deadline} onChange={(e) => setForm(prev => ({ ...prev, manager_review_deadline: e.target.value }))} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCycleDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="create-cycle-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
              Create Cycle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // Goal Card Component
  const GoalCard = ({ goal }) => {
    const [updating, setUpdating] = useState(false);

    const handleSubmitGoal = async () => {
      setUpdating(true);
      try {
        await performanceAPI.submitGoal(goal.id);
        toast.success('Goal submitted for approval');
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to submit goal');
      } finally {
        setUpdating(false);
      }
    };

    const handleApproveGoal = async () => {
      setUpdating(true);
      try {
        await performanceAPI.approveGoal(goal.id);
        toast.success('Goal approved');
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to approve goal');
      } finally {
        setUpdating(false);
      }
    };

    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                {goal.title}
              </CardTitle>
              <CardDescription className="mt-1">{goal.description}</CardDescription>
            </div>
            <Badge className={STATUS_COLORS[goal.status]}>{goal.status.replace('_', ' ')}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>Q{goal.quarter} {goal.year}</span>
              <span>•</span>
              <span>{goal.category}</span>
            </div>

            {goal.key_results?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Key Results</p>
                {goal.key_results.map((kr, idx) => (
                  <div key={kr.id || idx} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{kr.title}</span>
                      <span>{kr.current_value}/{kr.target_value} {kr.unit}</span>
                    </div>
                    <Progress value={kr.progress_percentage || 0} className="h-1.5" />
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Overall Progress</span>
                <Badge variant="outline">{goal.overall_progress || 0}%</Badge>
              </div>
              
              <div className="flex gap-2">
                {goal.status === 'draft' && (
                  <Button size="sm" onClick={handleSubmitGoal} disabled={updating}>
                    {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                    Submit
                  </Button>
                )}
                {goal.status === 'submitted' && isManager && (
                  <Button size="sm" onClick={handleApproveGoal} disabled={updating}>
                    {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    Approve
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Review Card Component
  const ReviewCard = ({ review, showActions = true }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold">{review.employee_name}</h3>
            <p className="text-sm text-muted-foreground">{review.designation} • {review.department}</p>
          </div>
          <Badge className={REVIEW_STATUS_COLORS[review.status]}>
            {review.status.replace(/_/g, ' ')}
          </Badge>
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Cycle</span>
            <span>{review.cycle_name}</span>
          </div>
          {review.final_rating && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Final Rating</span>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`w-4 h-4 ${i < review.final_rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                ))}
              </div>
            </div>
          )}
        </div>

        {showActions && (
          <div className="mt-4 flex gap-2">
            {review.status === 'manager_review_pending' && isManager && (
              <Button size="sm" onClick={() => { setSelectedReview(review); setReviewType('manager'); setShowReviewDialog(true); }}>
                <Edit2 className="w-4 h-4 mr-1" /> Review
              </Button>
            )}
            {review.status === 'hr_review_pending' && isHR && (
              <Button size="sm" onClick={() => { setSelectedReview(review); setReviewType('hr'); setShowReviewDialog(true); }}>
                <CheckCircle2 className="w-4 h-4 mr-1" /> Finalize
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => { setSelectedReview(review); }}>
              <Eye className="w-4 h-4 mr-1" /> View
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope']">Performance Management</h1>
          <p className="text-muted-foreground">Manage goals, OKRs, and performance reviews</p>
        </div>
        {isHR && (
          <Button onClick={() => setShowCycleDialog(true)} data-testid="create-cycle-btn">
            <Calendar className="w-4 h-4 mr-2" /> Create Review Cycle
          </Button>
        )}
      </div>

      {currentCycle && (
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">Active Review Cycle: {currentCycle.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Self review deadline: {new Date(currentCycle.self_review_deadline).toLocaleDateString()}
                  </p>
                </div>
              </div>
              {myReview?.status === 'self_review_pending' && (
                <Button onClick={() => { setSelectedReview(myReview); setReviewType('self'); setShowReviewDialog(true); }} data-testid="start-self-review-btn">
                  Start Self Review <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="my-goals" data-testid="my-goals-tab">
            <Target className="w-4 h-4 mr-2" /> My Goals
          </TabsTrigger>
          {isManager && (
            <TabsTrigger value="team-reviews" data-testid="team-reviews-tab">
              <Users className="w-4 h-4 mr-2" /> Team Reviews
            </TabsTrigger>
          )}
          {isHR && (
            <TabsTrigger value="hr-dashboard" data-testid="hr-dashboard-tab">
              <TrendingUp className="w-4 h-4 mr-2" /> HR Dashboard
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="my-goals" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">My Goals & OKRs</h2>
            <Button onClick={() => setShowGoalDialog(true)} data-testid="create-goal-btn">
              <Plus className="w-4 h-4 mr-2" /> New Goal
            </Button>
          </div>

          {goals.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No goals yet</h3>
                <p className="text-muted-foreground mb-4">Create your first goal to get started</p>
                <Button onClick={() => setShowGoalDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" /> Create Goal
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {goals.map(goal => (
                <GoalCard key={goal.id} goal={goal} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="team-reviews" className="space-y-4">
          <h2 className="text-lg font-semibold">Team Reviews</h2>
          
          {teamReviews.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No pending reviews</h3>
                <p className="text-muted-foreground">All team reviews are up to date</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {teamReviews.map(review => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="hr-dashboard" className="space-y-6">
          {summary && (
            <div className="grid gap-4 md:grid-cols-5">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{summary.total_employees}</div>
                  <p className="text-sm text-muted-foreground">Total Employees</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-green-600">{summary.reviews_completed}</div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-blue-600">{summary.reviews_pending_self}</div>
                  <p className="text-sm text-muted-foreground">Pending Self</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-yellow-600">{summary.reviews_pending_manager}</div>
                  <p className="text-sm text-muted-foreground">Pending Manager</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-purple-600">{summary.reviews_pending_hr}</div>
                  <p className="text-sm text-muted-foreground">Pending HR</p>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Review Cycles</CardTitle>
              </CardHeader>
              <CardContent>
                {cycles.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No review cycles created yet</p>
                ) : (
                  <div className="space-y-3">
                    {cycles.map(cycle => (
                      <div key={cycle.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{cycle.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(cycle.start_date).toLocaleDateString()} - {new Date(cycle.end_date).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge variant={cycle.is_active ? 'default' : 'secondary'}>
                          {cycle.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pending HR Reviews</CardTitle>
              </CardHeader>
              <CardContent>
                {teamReviews.filter(r => r.status === 'hr_review_pending').length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No reviews pending HR action</p>
                ) : (
                  <div className="space-y-3">
                    {teamReviews.filter(r => r.status === 'hr_review_pending').slice(0, 5).map(review => (
                      <div key={review.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{review.employee_name}</p>
                          <p className="text-sm text-muted-foreground">{review.department}</p>
                        </div>
                        <Button size="sm" onClick={() => { setSelectedReview(review); setReviewType('hr'); setShowReviewDialog(true); }}>
                          Finalize
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <GoalDialog />
      <SelfReviewDialog />
      <ManagerReviewDialog />
      <HRReviewDialog />
      <CycleDialog />
    </div>
  );
}
